import React, { useMemo } from 'react';
import { get, isEmpty } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { ForgeDivider, ForgeSkeleton } from '@tylertech/forge-react';
import { ISelectOption } from '@tylertech/forge';
import { I18n } from 'common/i18n';
import { ProcessDiscoverySelect } from './AgGridUpdated/AgGridComponents/ProcessDiscoverySelect';
import { ActionSelectorCheckbox } from './ActionSelectorCheckbox';
import { MultiActionSelector } from './AgGridUpdated/AgGridComponents/MultiActionSelector';

import { useProcessDiscoveryResults } from 'common/visualizations/views/agGridReact/customHooks/useProcessDiscoveryResults';
import {
  setShowExistingActionColumn,
  setShowProcessDiscoveryAction,
  setSelectedExistingColumn,
  setSelectedActionForExistingColumn,
  setNewTableActions,
  setActionsPrimaryKeyColumn,
} from 'common/authoring_workflow/actions.js';
import {
  getShowProcessDiscoveryAction,
  getShowExistingActionColumn,
  getSelectedExistingColumn,
  getNewTableActions,
  getSelectedActionForExistingColumn,
  getActionsPrimaryKeyColumn,
  getTableColumns,
} from 'common/authoring_workflow/selectors/vifAuthoring.js';
import { AuthoringWorkflowState } from 'common/authoring_workflow/reducers/types';
import { getCurrentMetadata, getDisplayableColumns } from 'common/authoring_workflow/selectors/metadata';
import { getProcessDiscoveryContext } from 'common/visualizations/views/agGridReact/helpers/processDiscoveryHelper';
import './ProcessDiscovery.scss';
import { ViewColumn } from 'common/types/viewColumn';

const scope = 'shared.visualizations.panes.presentation.fields.actions';

export const ProcessDiscoveryActions = () => {
  const dispatch = useDispatch();

  const vifAuthoring = useSelector((state: AuthoringWorkflowState) => state.vifAuthoring);
  const metadata = useSelector((state: AuthoringWorkflowState) =>
    getCurrentMetadata(state.metadataCollection, state.vifAuthoring),
  );
  const { data } = metadata;

  const getDropdownOptions = (columns: ViewColumn[]): ISelectOption[] => {
    return columns.map((column: ViewColumn) => {
      return {
        label: column.name || '',
        value: column.fieldName,
      };
    });
  };

  const tableColumns = getTableColumns(vifAuthoring);
  const displayableColumns = getDisplayableColumns(metadata);
  const tableColumnDroptionOptions = getDropdownOptions(tableColumns);
  const primaryKeyColumnDroptionOptions = getDropdownOptions(displayableColumns);
  const contextParams = getProcessDiscoveryContext(data);
  const showExistingActionColumn = getShowExistingActionColumn(vifAuthoring);
  const showProcessDiscoveryAction = getShowProcessDiscoveryAction(vifAuthoring);
  const selectedExistingColumn = getSelectedExistingColumn(vifAuthoring);
  const selectedActionForExistingColumn = getSelectedActionForExistingColumn(vifAuthoring);
  const newTableActions = getNewTableActions(vifAuthoring);
  const actionsPrimaryKeyColumn = getActionsPrimaryKeyColumn(vifAuthoring);
  const primaryValue = isEmpty(data) ? '' : 'processDiscoveryActions';
  const { processDiscoveryResults, loading } = useProcessDiscoveryResults(primaryValue, contextParams, {
    showAllActions: true,
  });
  const { processDescriptors = [] } = get(processDiscoveryResults, '[0]', {});

  const actionOptions = useMemo(() => {
    return processDescriptors.map((processDescriptor: { name: string; id: string }) => {
      return {
        label: processDescriptor.name,
        value: processDescriptor.id,
      };
    });
  }, [processDescriptors]);

  const onColumnSelect = (event: CustomEvent) => {
    dispatch(setSelectedExistingColumn(event.detail));
  };

  const onActionSelect = (event: CustomEvent) => {
    dispatch(setSelectedActionForExistingColumn(event.detail));
  };

  const onNewActionClick = () => {
    dispatch(setShowProcessDiscoveryAction(!showProcessDiscoveryAction));
  };

  const onExistingActionClick = () => {
    dispatch(setShowExistingActionColumn(!showExistingActionColumn));
  };

  const onMultipleActionSelect = (actions: string[]) => {
    dispatch(setNewTableActions(actions));
  };

  const onPrimaryKeyColumnSelect = (event: CustomEvent) => {
    dispatch(setActionsPrimaryKeyColumn(event.detail));
  };

  return (
    <>
      <div className="authoring-field process-discovery" key="actionColumnSelector">
        <div className="action-column-selector-container">
          <div className="actions-primary-key-label forge-typography--subheading1">Action linking column</div>
          <ProcessDiscoverySelect
            options={primaryKeyColumnDroptionOptions}
            placeHolder={I18n.t('existing.column_placeholder', { scope })}
            value={actionsPrimaryKeyColumn}
            onChange={onPrimaryKeyColumnSelect}
            label="Primary Key Column"
          />

          {actionsPrimaryKeyColumn && (
            <>
              <div className="actions-header forge-typography--subheading1">
                {I18n.t('enable_in', { scope })}
              </div>
              <ActionSelectorCheckbox
                dense={false}
                label={I18n.t('new_action_column', { scope })}
                isChecked={showProcessDiscoveryAction}
                onClick={onNewActionClick}
              />
              {showProcessDiscoveryAction &&
                (loading ? (
                  <ForgeSkeleton form-field></ForgeSkeleton>
                ) : (
                  <>
                    <MultiActionSelector
                      options={actionOptions}
                      label={I18n.t('title', { scope })}
                      value={newTableActions}
                      onChange={onMultipleActionSelect}
                      loading={false}
                    />
                    <ForgeDivider />
                  </>
                ))}
              <ActionSelectorCheckbox
                dense={false}
                label={I18n.t('existing.title', { scope })}
                isChecked={showExistingActionColumn}
                onClick={onExistingActionClick}
              />
              {showExistingActionColumn && (
                <>
                  <ProcessDiscoverySelect
                    options={tableColumnDroptionOptions}
                    placeHolder={I18n.t('existing.column_placeholder', { scope })}
                    value={selectedExistingColumn}
                    onChange={onColumnSelect}
                    label={I18n.t('existing.column_label', { scope })}
                  />
                  <ProcessDiscoverySelect
                    options={actionOptions}
                    placeHolder={I18n.t('existing.action_placeholder', { scope })}
                    label={I18n.t('existing.action_label', { scope })}
                    value={selectedActionForExistingColumn}
                    onChange={onActionSelect}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
